import React from "react";
import { useTranslation } from "react-i18next";
import {
  VARIANT_FILL,
  VARIANT_STROKE,
  NEUTRAL_FILL,
  SYMBOL_STROKE,
  LINE,
  LABEL,
  DiagramFigure,
  Legend,
  LegendItem,
} from "./diagramTheme";

// Sized to the drawing rather than to a nominal canvas: content runs from the
// generation labels at x=14 to III-2's right edge at x=320, so a wider box just
// scales the symbols down and strands empty space beside them.
const VIEW_W = 340;
const VIEW_H = 292;
const SIZE = 30; // square side, and circle diameter, so both read as one scale

const GEN_Y = [46, 148, 250];

// Standard medical pedigree notation: square for male, circle for female,
// a solid fill for someone with the condition, a centre dot for a carrier,
// and an arrow marking the consultand — the person the assessment is about.
const PEOPLE = [
  { id: "I-1", x: 150, gen: 0, sex: "male", status: "unaffected" },
  { id: "I-2", x: 240, gen: 0, sex: "female", status: "carrier" },

  { id: "II-1", x: 105, gen: 1, sex: "male", status: "affected" },
  { id: "II-2", x: 205, gen: 1, sex: "female", status: "carrier" },
  { id: "II-3", x: 300, gen: 1, sex: "male", status: "unaffected" },

  // Both generation III siblings are untested, which is the point of the
  // example: the consultand's own status is the open question the assessment
  // exists to address. They carry the same question mark for that reason.
  { id: "III-1", x: 215, gen: 2, sex: "female", status: "unknown", consultand: true },
  { id: "III-2", x: 305, gen: 2, sex: "male", status: "unknown" },
];

const at = (id) => PEOPLE.find((p) => p.id === id);

const PedigreeSymbol = ({ person }) => {
  const cy = GEN_Y[person.gen];
  const isAffected = person.status === "affected";
  const fill = isAffected ? VARIANT_FILL : NEUTRAL_FILL;
  const stroke = isAffected ? VARIANT_STROKE : SYMBOL_STROKE;
  const half = SIZE / 2;
  return (
    <g>
      {person.sex === "male" ? (
        <rect
          x={person.x - half}
          y={cy - half}
          width={SIZE}
          height={SIZE}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.8}
        />
      ) : (
        <circle
          cx={person.x}
          cy={cy}
          r={half}
          fill={fill}
          stroke={stroke}
          strokeWidth={1.8}
        />
      )}
      {person.status === "carrier" && (
        <circle cx={person.x} cy={cy} r={4} fill={VARIANT_STROKE} />
      )}
      {person.status === "unknown" && (
        <text
          x={person.x}
          y={cy + 5}
          textAnchor="middle"
          fontSize={15}
          fill={LABEL}
        >
          ?
        </text>
      )}
      {person.consultand && (
        <g>
          <path
            d={`M ${person.x - half - 20} ${cy + half + 18} L ${person.x - half - 3} ${cy + half + 2}`}
            stroke={LABEL}
            strokeWidth={1.8}
            markerEnd="url(#pedigree-arrow)"
          />
        </g>
      )}
    </g>
  );
};

// Partner line between two people on the same generation.
const Partnership = ({ leftId, rightId }) => {
  const a = at(leftId);
  const b = at(rightId);
  const y = GEN_Y[a.gen];
  return (
    <path
      d={`M ${a.x + SIZE / 2} ${y} L ${b.x - SIZE / 2} ${y}`}
      stroke={LINE}
      strokeWidth={1.5}
    />
  );
};

// Drop from a couple's midpoint to a sibship bar, then one drop per child.
const Sibship = ({ parents, childIds }) => {
  const [pa, pb] = parents.map(at);
  const parentY = GEN_Y[pa.gen];
  const midX = (pa.x + pb.x) / 2;
  const children = childIds.map(at);
  const childY = GEN_Y[children[0].gen];
  const barY = childY - 34;
  const left = Math.min(...children.map((c) => c.x));
  const right = Math.max(...children.map((c) => c.x));
  return (
    <g>
      <path
        d={`M ${midX} ${parentY} L ${midX} ${barY} M ${left} ${barY} L ${right} ${barY}`}
        stroke={LINE}
        strokeWidth={1.5}
        fill="none"
      />
      {children.map((c) => (
        <path
          key={c.id}
          d={`M ${c.x} ${barY} L ${c.x} ${childY - SIZE / 2}`}
          stroke={LINE}
          strokeWidth={1.5}
        />
      ))}
    </g>
  );
};

const SwatchBox = ({ children }) => (
  <svg width="26" height="26" aria-hidden="true" focusable="false">
    {children}
  </svg>
);

function PedigreeDiagram({ serviceKey = "reproductiveRisk" }) {
  const { t } = useTranslation();
  const base = `landing.service.${serviceKey}.diagram`;

  return (
    <DiagramFigure
      base={base}
      t={t}
      legend={
        <Legend>
          <LegendItem
            swatch={
              <SwatchBox>
                <rect
                  x={4}
                  y={4}
                  width={18}
                  height={18}
                  fill={NEUTRAL_FILL}
                  stroke={SYMBOL_STROKE}
                  strokeWidth={1.8}
                />
              </SwatchBox>
            }
          >
            {t(`${base}.legend.male`)}
          </LegendItem>
          <LegendItem
            swatch={
              <SwatchBox>
                <circle
                  cx={13}
                  cy={13}
                  r={9}
                  fill={NEUTRAL_FILL}
                  stroke={SYMBOL_STROKE}
                  strokeWidth={1.8}
                />
              </SwatchBox>
            }
          >
            {t(`${base}.legend.female`)}
          </LegendItem>
          <LegendItem
            swatch={
              <SwatchBox>
                <circle
                  cx={13}
                  cy={13}
                  r={9}
                  fill={NEUTRAL_FILL}
                  stroke={SYMBOL_STROKE}
                  strokeWidth={1.8}
                />
                <circle cx={13} cy={13} r={3.5} fill={VARIANT_STROKE} />
              </SwatchBox>
            }
          >
            {t(`${base}.legend.carrier`)}
          </LegendItem>
          <LegendItem
            swatch={
              <SwatchBox>
                <rect
                  x={4}
                  y={4}
                  width={18}
                  height={18}
                  fill={VARIANT_FILL}
                  stroke={VARIANT_STROKE}
                  strokeWidth={1.8}
                />
              </SwatchBox>
            }
          >
            {t(`${base}.legend.affected`)}
          </LegendItem>
          <LegendItem
            swatch={
              <SwatchBox>
                <rect
                  x={4}
                  y={4}
                  width={18}
                  height={18}
                  fill={NEUTRAL_FILL}
                  stroke={SYMBOL_STROKE}
                  strokeWidth={1.8}
                />
                <text
                  x={13}
                  y={18}
                  textAnchor="middle"
                  fontSize={13}
                  fill={LABEL}
                >
                  ?
                </text>
              </SwatchBox>
            }
          >
            {t(`${base}.legend.unknown`)}
          </LegendItem>
          <LegendItem
            swatch={
              <SwatchBox>
                <path
                  d="M 3 22 L 17 9"
                  stroke={LABEL}
                  strokeWidth={1.8}
                  fill="none"
                />
                <path d="M 21 5 L 14 8 L 18 12 Z" fill={LABEL} />
              </SwatchBox>
            }
          >
            {t(`${base}.legend.consultand`)}
          </LegendItem>
        </Legend>
      }
    >
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        width="100%"
        role="img"
        aria-labelledby="pedigree-title"
        style={{ maxWidth: "500px", height: "auto", display: "block" }}
      >
        {/* The full prose description lives in DiagramFigure's sr-only
            paragraph. Repeating it in a <desc> here would make screen readers
            announce the whole paragraph twice, once before the image and again
            as the image's own name. */}
        <title id="pedigree-title">{t(`${base}.title`)}</title>

        <defs>
          <marker
            id="pedigree-arrow"
            markerWidth="7"
            markerHeight="7"
            refX="5"
            refY="3.5"
            orient="auto"
          >
            <path d="M 0 0 L 7 3.5 L 0 7 Z" fill={LABEL} />
          </marker>
        </defs>

        {/* generation labels down the left edge */}
        {GEN_Y.map((y, i) => (
          <text
            key={`gen-${i}`}
            x={14}
            y={y + 5}
            fontSize={13}
            fontWeight="bold"
            // These are read alongside the prose, which refers to "generation
            // I/II/III", so they take the text colour. LINE is for connectors
            // and is only 1.8:1 on the white section behind it.
            fill={LABEL}
          >
            {["I", "II", "III"][i]}
          </text>
        ))}

        <Partnership leftId="I-1" rightId="I-2" />
        <Sibship parents={["I-1", "I-2"]} childIds={["II-1", "II-2"]} />
        <Partnership leftId="II-2" rightId="II-3" />
        <Sibship parents={["II-2", "II-3"]} childIds={["III-1", "III-2"]} />

        {PEOPLE.map((person) => (
          <PedigreeSymbol key={person.id} person={person} />
        ))}
      </svg>

      <p style={{ fontSize: "15px", color: "#666", marginTop: "16px" }}>
        {t(`${base}.reading`)}
      </p>
    </DiagramFigure>
  );
}

export default PedigreeDiagram;
